import styled from 'styled-components';

import { Text } from '../../../../components';
import { COLORS, SPACE } from '../../../../theme/tokens';

/* The blog editor's toolbar look (`blog-editor/.../MarkdownEditor.styles.ts`), replicated on SPACE: that file also
 * carries the blog's generating washes, so importing it would couple this page to the blog editor. */

export const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
`;

/** Field-like surface hosting the toolbar + the ProseMirror content area; muted while it only shows a text. */
export const Frame = styled.div<{ $readOnly: boolean }>`
  border: 1px solid ${COLORS.panelBorder};
  border-radius: 12px;
  background: ${(p) => (p.$readOnly ? 'transparent' : COLORS.surfaceInset)};
  overflow: hidden;
`;

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[2]};
  padding: ${SPACE[8]};
  border-bottom: 1px solid ${COLORS.hairline};
`;

export const ToolGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[2]};

  & + &::before {
    content: '';
    width: 1px;
    height: 18px;
    /* 6px: centres the 1px divider between the 2px group gaps (optical, as the blog toolbar). */
    margin: 0 6px;
    background: ${COLORS.hairline};
  }
`;

export const ToolButton = styled.button<{ $active?: boolean }>`
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: ${(p) => (p.$active ? COLORS.surfaceMuted : 'transparent')};
  color: ${(p) => (p.$active ? COLORS.textBright : COLORS.icon)};
  font-size: 15px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/** The Q&A insert: a tool with its name written out, since a glyph alone does not say "question and answer". */
export const TextTool = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${SPACE[4]};
  height: 30px;
  padding: 0 ${SPACE[8]};
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${COLORS.surfaceMuted};
  }

  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;

/** Inline URL row revealed by the link tool. */
export const LinkRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACE[8]};
  padding: ${SPACE[8]};
  border-bottom: 1px solid ${COLORS.hairline};
`;

export const LinkInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: ${COLORS.text};
  font-size: 14px;
  font-family: inherit;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
`;

export const LinkAction = styled.button`
  border: none;
  background: transparent;
  color: ${COLORS.ctaText};
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  padding: ${SPACE[4]} ${SPACE[8]};
  border-radius: 8px;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: ${COLORS.surfaceMuted};
  }
`;

export const LinkError = styled(Text)`
  display: block;
  padding: ${SPACE[4]} ${SPACE[8]} ${SPACE[8]};
  font-size: 12px;
  color: ${COLORS.danger};
`;

/** Grows with the text up to a cap, then scrolls; the footer text is a few paragraphs, not an article. */
export const Content = styled.div<{ $readOnly: boolean }>`
  max-height: 480px;
  overflow-y: auto;
  overscroll-behavior: contain;

  .ProseMirror {
    min-height: ${(p) => (p.$readOnly ? 'auto' : '160px')};
    box-sizing: border-box;
    padding: ${SPACE[16]};
    outline: none;
    color: ${(p) => (p.$readOnly ? COLORS.textMuted : COLORS.text)};
    font-size: 14px;
    line-height: 1.6;

    > :first-child {
      margin-top: 0;
    }

    > :last-child {
      margin-bottom: 0;
    }

    h2,
    h3 {
      color: ${(p) => (p.$readOnly ? COLORS.textMuted : COLORS.heading)};
      line-height: 1.25;
      margin: ${SPACE[20]} 0 ${SPACE[8]};
    }

    h2 {
      font-size: 20px;
    }

    h3 {
      font-size: 16px;
    }

    p {
      margin: ${SPACE[8]} 0;
    }

    ul,
    ol {
      padding-left: ${SPACE[20]};
      margin: ${SPACE[8]} 0;
    }

    a {
      color: ${COLORS.accent};
      text-decoration: underline;
    }
  }
`;
